import type { AxiosError } from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { api } from '../../api';
import {
  getAskForFirstAlertsCommunityModal,
  getAskForFirstAlertsStartModal,
} from '../../components/FirstAlerts/first-alerts.helper';
import { getViewInternalFACommunities } from '../../helpers/device.helper';
import type { ErrorMessage } from '../../models/Error';
import type { AdvocateAvailability, FirstAlertsCommunity, GuestFirstAlertsCommunity } from '../../models/guest';
import type { DartNotificationContent, NotificationContent } from '../../models/notification';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun';

const { logError } = RaygunErrorHandlerService();

export const FA_MODAL = {
  IDLE: 'idle',
  DISCLAIMER: 'fa_disclaimer',
  ONBOARDING: 'fa_onboarding',
  START: 'fa_start',
  NEW_COMMUNITY: 'fa_new_community',
} as const;

export type FA_MODAL = (typeof FA_MODAL)[keyof typeof FA_MODAL];

export type SentAlert = Omit<NotificationContent, 'alert_id'> & { id: string };

export type SubscriptionAction =
  | { type: 'one'; isSubscribed: boolean; community: FirstAlertsCommunity }
  | { type: 'all'; isSubscribed: boolean };

type FirstAlertsSliceType = {
  faModal: FA_MODAL;
  guestAlertPromptResponse: boolean | null | undefined;
  guestAlertAcceptedDisclaimer: boolean | null | undefined;
  guestAlertNotification: NotificationContent | undefined;
  allFirstAlertsCommunityList: FirstAlertsCommunity[];
  guestFirstAlertsCommunityList: GuestFirstAlertsCommunity[];
  pendingSubscriptionAction: SubscriptionAction | undefined;
  guestFirstAlertsAskingAgain: boolean;
  guestFirstAlertsHasNewCommunity: boolean | undefined;
  dartAlertInfo: DartNotificationContent | undefined;
  dartAdvocateAvailability: AdvocateAvailability | undefined;
  showGuestFirstAlertsCommunityNotFoundModal: boolean;
  showDrawerFromPush: boolean;
  guestAlertsLoading: boolean;
  firstAlertsCommunityListLoading: boolean;
  guestAlerts: SentAlert[];
  isSubscribedToAll: boolean;
  showNotificationDisabledModal: boolean;
};

const initialState: FirstAlertsSliceType = {
  faModal: FA_MODAL.IDLE,
  guestAlertPromptResponse: undefined,
  guestAlertAcceptedDisclaimer: undefined,
  guestAlertNotification: undefined,
  allFirstAlertsCommunityList: [],
  guestFirstAlertsCommunityList: [],
  pendingSubscriptionAction: undefined,
  guestFirstAlertsAskingAgain: false,
  guestFirstAlertsHasNewCommunity: undefined,
  dartAlertInfo: undefined,
  dartAdvocateAvailability: undefined,
  showGuestFirstAlertsCommunityNotFoundModal: false,
  showDrawerFromPush: false,
  guestAlertsLoading: false,
  firstAlertsCommunityListLoading: false,
  guestAlerts: [],
  isSubscribedToAll: false,
  showNotificationDisabledModal: false,
};

export const getFirstAlertsCommunityList = createAsyncThunk<FirstAlertsCommunity[], undefined, AsyncThunkConfig>(
  'firstAlerts/getFirstAlertsCommunityList',
  async (_, thunkAPI) => {
    try {
      const view_internal_fa_communities = getViewInternalFACommunities();
      const response = await api.post<FirstAlertsCommunity[]>('/guest/v1_get_first_alerts_communities', {
        view_internal_fa_communities,
      });
      return response ?? [];
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'getFirstAlertsCommunityList']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getGuestFirstAlertsCommunityList = createAsyncThunk<
  GuestFirstAlertsCommunity[],
  undefined,
  AsyncThunkConfig
>('firstAlerts/getGuestFirstAlertsCommunityList', async (_, thunkAPI) => {
  try {
    const view_internal_fa_communities = getViewInternalFACommunities();
    const response = await api.post<GuestFirstAlertsCommunity[]>('/guest/v0_get_guest_first_alerts_communities', {
      view_internal_fa_communities,
    });
    return response ?? [];
  } catch (e) {
    logError(e, ['firstAlertsSlice', 'getGuestFirstAlertsCommunityList']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const resetFirstAlerts = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'firstAlerts/resetFirstAlerts',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<string>('/guest/v0_reset_first_alerts');
      await thunkAPI.dispatch(getGuestFirstAlertsCommunityList());
      await thunkAPI.dispatch(getAlertPromptResponse());
      await thunkAPI.dispatch(getAlertAcceptedDisclaimer());
      return response ?? '';
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'resetFirstAlerts']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

// oxlint-disable-next-line typescript/no-invalid-void-type
export const checkFAOnboarding = createAsyncThunk<void, undefined, AsyncThunkConfig>(
  'firstAlerts/checkFAOnboarding',
  async (_, thunkAPI) => {
    try {
      const response = await thunkAPI.dispatch(getAlertPromptResponse());

      if (getAlertPromptResponse.fulfilled.match(response)) {
        const guestAlertPromptResponse = response.payload;
        const askAgainForFirstAlertsStart = guestAlertPromptResponse === false && getAskForFirstAlertsStartModal();
        const showFirstAlertStartModal = guestAlertPromptResponse === null || askAgainForFirstAlertsStart;

        if (showFirstAlertStartModal) {
          thunkAPI.dispatch(setGuestFirstAlertsAskingAgain(askAgainForFirstAlertsStart));
          thunkAPI.dispatch(setFAModal(FA_MODAL.START));
        }
      }
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'checkFAOnboarding']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const deleteGuestFirstAlertsCommunity = createAsyncThunk<boolean, string, AsyncThunkConfig>(
  'firstAlerts/deleteGuestFirstAlertsCommunity',
  async (first_alerts_community_id, thunkAPI) => {
    try {
      const response = await api.post<boolean>('/guest/v0_delete_guest_first_alerts_community', {
        first_alerts_community_id,
      });
      await thunkAPI.dispatch(getFASubscribedAll());
      await thunkAPI.dispatch(getGuestFirstAlertsCommunityList());
      return response ?? false;
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'deleteGuestFirstAlertsCommunity']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const saveGuestFirstAlertsCommunity = createAsyncThunk<boolean, string, AsyncThunkConfig>(
  'firstAlerts/saveGuestFirstAlertsCommunity',
  async (first_alerts_community_id, thunkAPI) => {
    try {
      const { allFirstAlertsCommunityList, guestFirstAlertsCommunityList } = thunkAPI.getState().firstAlertsSlice;
      const fa_subscribed_all = allFirstAlertsCommunityList.length === guestFirstAlertsCommunityList.length + 1;
      const response = await api.post<boolean>('/guest/v1_save_guest_first_alerts_community', {
        first_alerts_community_id,
        fa_subscribed_all,
      });
      await thunkAPI.dispatch(getFASubscribedAll());
      await thunkAPI.dispatch(getGuestFirstAlertsCommunityList());
      return response ?? false;
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'saveGuestFirstAlertsCommunity']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const setAlertPromptResponse = createAsyncThunk<string, boolean, AsyncThunkConfig>(
  'firstAlerts/setAlertPromptResponse',
  async (prompt_response, thunkAPI) => {
    try {
      const response = await api.post<string>('/guest/v0_set_alert_prompt_response', { prompt_response });
      if (response) {
        await thunkAPI.dispatch(getAlertPromptResponse());
      }
      return response ?? ''; // guest_id
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'setAlertPromptResponse']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getAlertPromptResponse = createAsyncThunk<boolean | null | undefined, undefined, AsyncThunkConfig>(
  'firstAlerts/getAlertPromptResponse',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<boolean>('/guest/v0_get_alert_prompt_response');
      return response;
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'getAlertPromptResponse']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const setAlertAcceptedDisclaimer = createAsyncThunk<string, boolean, AsyncThunkConfig>(
  'firstAlerts/setAlertAcceptedDisclaimer',
  async (accepted_disclaimer, thunkAPI) => {
    try {
      const response = await api.post<string>('/guest/v0_set_alert_accepted_disclaimer', { accepted_disclaimer });
      if (response) {
        await thunkAPI.dispatch(getAlertAcceptedDisclaimer());
      }
      return response ?? ''; // guest_id
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'setAlertAcceptedDisclaimer']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getAlertAcceptedDisclaimer = createAsyncThunk<boolean | null | undefined, undefined, AsyncThunkConfig>(
  'firstAlerts/getAlertAcceptedDisclaimer',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<boolean>('/guest/v0_get_alert_accepted_disclaimer');
      return response;
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'getAlertAcceptedDisclaimer']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const checkHasNewFirstAlertsCommunity = createAsyncThunk<boolean, undefined, AsyncThunkConfig>(
  'firstAlerts/checkHasNewFirstAlertsCommunity',
  async (_, thunkAPI) => {
    try {
      const prompt_date = getAskForFirstAlertsCommunityModal();
      if (!prompt_date) return false;
      const response = await api.post<boolean>('/guest/v1_check_latest_first_alerts_community', { prompt_date });
      return response ?? false;
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'checkHasNewFirstAlertsCommunity']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getDartAlertInfo = createAsyncThunk<DartNotificationContent | undefined, undefined, AsyncThunkConfig>(
  'firstAlerts/getDartAlertInfo',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<DartNotificationContent | undefined>('/guest/v0_get_dart_alert_info');
      return response;
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'getDartAlertInfo']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getDartAdvocateAvailability = createAsyncThunk<
  AdvocateAvailability | undefined,
  undefined,
  AsyncThunkConfig
>('firstAlerts/getDartAdvocateAvailability', async (_, thunkAPI) => {
  try {
    const response = await api.post<AdvocateAvailability | undefined>('/guest/v0_get_dart_advocate_availability');
    return response;
  } catch (e) {
    logError(e, ['firstAlertsSlice', 'getDartAdvocateAvailability']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const getGuestAlerts = createAsyncThunk<SentAlert[], undefined, AsyncThunkConfig>(
  'firstAlerts/getGuestAlerts',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<SentAlert[]>('/guest/v0_get_alerts');
      return response ?? [];
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'getGuestAlerts']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getFASubscribedAll = createAsyncThunk<boolean, undefined, AsyncThunkConfig>(
  'firstAlerts/getFASubscribedAll',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<boolean>('/guest/v0_get_fa_subscribed_all');
      return response ?? false;
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'getFASubscribedAll']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const setFASubscribedAll = createAsyncThunk<boolean, boolean, AsyncThunkConfig>(
  'firstAlerts/setFASubscribedAll',
  async (fa_subscribed_all, thunkAPI) => {
    try {
      const view_internal_fa_communities = getViewInternalFACommunities();
      const response = await api.post<boolean>('/guest/v0_set_fa_subscribed_all', {
        fa_subscribed_all,
        view_internal_fa_communities,
      });
      await thunkAPI.dispatch(getGuestFirstAlertsCommunityList());
      return response ?? false;
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'setFASubscribedAll']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendSubscription = createAsyncThunk<{ event: string }, SubscriptionAction, AsyncThunkConfig>(
  'firstAlerts/handleSubscribeUnsubscribe',
  async (action, thunkAPI) => {
    try {
      let event = '';
      if (action.type === 'one') {
        if (action.isSubscribed) {
          await thunkAPI.dispatch(deleteGuestFirstAlertsCommunity(action.community.id));
          event = 'Subscriptions unsubscribed from a FirstAlert community';
        } else {
          await thunkAPI.dispatch(saveGuestFirstAlertsCommunity(action.community.id));
          event = 'Subscriptions subscribed to a FirstAlert community';
        }
      } else {
        await thunkAPI.dispatch(setFASubscribedAll(!action.isSubscribed));
        event = `Subscriptions toggled to ${action.isSubscribed ? 'unsubscribe all' : 'subscribe all'}`;
      }
      thunkAPI.dispatch(setPendingSubscriptionAction(undefined));
      return { event };
    } catch (e) {
      logError(e, ['firstAlertsSlice', 'handleSubscribeUnsubscribe']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const firstAlertsSlice = createSlice({
  name: 'firstAlerts',
  initialState,
  reducers: {
    setGuestAlertNotification: (state, action: PayloadAction<NotificationContent | undefined>) => {
      state.guestAlertNotification = action.payload;
    },
    setGuestFirstAlertsAskingAgain: (state, action: PayloadAction<boolean>) => {
      state.guestFirstAlertsAskingAgain = action.payload;
    },
    setShowDrawerFromPush: (state, action: PayloadAction<boolean>) => {
      state.showDrawerFromPush = action.payload;
    },
    setGuestFirstAlertsCommunityNotFoundModal: (state, action: PayloadAction<boolean>) => {
      state.showGuestFirstAlertsCommunityNotFoundModal = action.payload;
    },
    setDartAlertInfo: (state, action: PayloadAction<DartNotificationContent | undefined>) => {
      state.dartAlertInfo = action.payload;
    },
    setGuestFirstAlertsHasNewCommunity: (state, action: PayloadAction<boolean>) => {
      state.guestFirstAlertsHasNewCommunity = action.payload;
    },
    setPendingSubscriptionAction: (state, action: PayloadAction<SubscriptionAction | undefined>) => {
      state.pendingSubscriptionAction = action.payload;
    },
    setShowNotificationDisabledModal: (state, action: PayloadAction<boolean>) => {
      state.showNotificationDisabledModal = action.payload;
    },
    setFAModal: (state, action: PayloadAction<FA_MODAL>) => {
      state.faModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFirstAlertsCommunityList.pending, (state, _action) => {
        state.firstAlertsCommunityListLoading = true;
      })
      .addCase(getFirstAlertsCommunityList.fulfilled, (state, action) => {
        state.allFirstAlertsCommunityList = action.payload;
        state.firstAlertsCommunityListLoading = false;
      })
      .addCase(getGuestFirstAlertsCommunityList.fulfilled, (state, action) => {
        state.guestFirstAlertsCommunityList = action.payload;
      })
      .addCase(getAlertPromptResponse.fulfilled, (state, action) => {
        state.guestAlertPromptResponse = action.payload;
      })
      .addCase(getAlertAcceptedDisclaimer.fulfilled, (state, action) => {
        state.guestAlertAcceptedDisclaimer = action.payload;
      })
      .addCase(checkHasNewFirstAlertsCommunity.fulfilled, (state, action) => {
        state.guestFirstAlertsHasNewCommunity = action.payload;
      })
      .addCase(getDartAlertInfo.fulfilled, (state, action) => {
        state.dartAlertInfo = action.payload;
      })
      .addCase(getDartAdvocateAvailability.fulfilled, (state, action) => {
        state.dartAdvocateAvailability = action.payload;
      })
      .addCase(getGuestAlerts.pending, (state, _action) => {
        state.guestAlertsLoading = true;
      })
      .addCase(getGuestAlerts.fulfilled, (state, action) => {
        state.guestAlerts = action.payload;
        state.guestAlertsLoading = false;
      })
      .addCase(getFASubscribedAll.fulfilled, (state, action) => {
        state.isSubscribedToAll = action.payload;
      })
      .addCase(setFASubscribedAll.fulfilled, (state, action) => {
        state.isSubscribedToAll = action.payload;
      });
  },
});

export const {
  setDartAlertInfo,
  setFAModal,
  setGuestAlertNotification,
  setGuestFirstAlertsAskingAgain,
  setGuestFirstAlertsCommunityNotFoundModal,
  setGuestFirstAlertsHasNewCommunity,
  setPendingSubscriptionAction,
  setShowDrawerFromPush,
  setShowNotificationDisabledModal,
} = firstAlertsSlice.actions;
